import {ResponsiveLayout} from '@frontend/components/ResponsiveLayout'
import {trans} from '@frontend/helpers'
import type {AdLocation} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useAdsStore} from '@frontend/stores'
import {
    useEffect,
    useMemo,
    useState,
} from 'react'
import {useDebounce} from 'rooks'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Input} from 'swayok-react-mdb-ui-kit/components/Input/Input'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {ModalFooter} from 'swayok-react-mdb-ui-kit/components/Modal/ModalFooter'
import {ModalHeader} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeader'
import {LocationsSelectorOptionsDesktop} from './LocationsSelectorOptionsDesktop'
import {LocationsSelectorOptionsMobile} from './LocationsSelectorOptionsMobile'

interface Props {
    currentLocation: AdLocation | null
    onClose: () => void
    onLocationChange: (location: AdLocation) => void
}

// Компонент для выбора локации для поиска объявлений в ней.
export function LocationSelectorModalContent(props: Props) {

    const locations = useAdsStore(
        state => state.locations
    )

    const {
        onClose,
        onLocationChange,
        currentLocation,
    } = props

    // Поиск локации.
    const [
        keywordsRegexp,
        setKeywordsRegexp,
    ] = useState<RegExp | null>(null)

    // Слегка отложенное изменение keywordsRegexp.
    const setKeywordsRegexpDebounced = useDebounce(
        setKeywordsRegexp,
        300
    )

    // Выбранная локация.
    const [
        selectedLocation,
        setSelectedLocation,
    ] = useState<AdLocation | null>(currentLocation)

    useEffect(() => {
        setSelectedLocation(currentLocation)
    }, [currentLocation])

    // Элементы локаций по уровням (закешированные).
    const levels: AdLocation[][] = useMemo(function () {
        const levels: AdLocation[][] = []
        collectOptionsByLevels(
            0,
            locations.tree,
            levels
        )
        return levels
    }, [locations])

    return (
        <ModalContent>
            <ModalHeader
                title={trans().common.ads_filter.locations_modal.header}
                onClose={onClose}
            />
            <ModalBody className="p-0 flex-container-column overflow-hidden">
                <div className="p-3">
                    <Input
                        label={trans().common.ads_filter.locations_modal.search_location}
                        type="search"
                        wrapperClassName="p-0"
                        active={!!keywordsRegexp}
                        onChange={event => {
                            setKeywordsRegexpDebounced(
                                makeRegexpFromKeywords(event.currentTarget.value)
                            )
                        }}
                    />
                </div>
                <ResponsiveLayout
                    desktop={() => (
                        <LocationsSelectorOptionsDesktop
                            levels={levels}
                            headers={locations.levels}
                            selectedLocation={selectedLocation}
                            keywordsRegexp={keywordsRegexp}
                            onSelect={setSelectedLocation}
                        />
                    )}
                    mobile={() => (
                        <LocationsSelectorOptionsMobile
                            levels={levels}
                            headers={locations.levels}
                            selectedLocation={selectedLocation}
                            keywordsRegexp={keywordsRegexp}
                            onSelect={setSelectedLocation}
                        />
                    )}
                />
            </ModalBody>
            <ModalFooter flexBetween>
                <ResponsiveLayout
                    desktop={
                        <Button
                            color="gray"
                            onClick={onClose}
                        >
                            {trans().common.modal.close}
                        </Button>
                    }
                    mobile={() => null}
                />
                <Button
                    color="blue"
                    className="full-width-sm-down"
                    disabled={!selectedLocation}
                    onClick={() => {
                        if (!selectedLocation) {
                            return
                        }
                        onLocationChange(selectedLocation)
                        onClose()
                    }}
                >
                    {trans().common.ads_filter.locations_modal.submit_location}
                </Button>
            </ModalFooter>
        </ModalContent>
    )
}

// Конвертация ключевых слов в регулярное выражение.
function makeRegexpFromKeywords(keywords: string | null): RegExp | null {
    if (!keywords || keywords.trim().length === 0) {
        return null
    }
    keywords = keywords.trim()
        .replace(/[.,:?*&(){}|#@$^[\]/\\-]/u, ' ')
        .replace(/\s+/u, '.+')
    return new RegExp(keywords, 'iu')
}

// Распределение локаций по уровням (рекурсивно).
function collectOptionsByLevels(
    groupIndex: number,
    locations: AdLocation[],
    levels: AdLocation[][]
): void {
    if (!levels[groupIndex]) {
        levels[groupIndex] = groupIndex === 0
            ? [
                {
                    id: 0,
                    name: trans().common.ads_filter.locations_modal.any_location,
                    parentIds: [],
                    children: [],
                },
            ]
            : []
    }
    for (const location of locations) {
        levels[groupIndex].push(location)
        if (location.children.length > 0) {
            collectOptionsByLevels(
                groupIndex + 1,
                location.children,
                levels
            )
        }
    }
}
